import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const formatDate = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return { day: "—", month: "" };
  return {
    day: d.toLocaleDateString("en-GB", { day: "2-digit" }),
    month: d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase(),
  };
};

export const EventsBoard = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((res) => setEvents(res.data)).catch(() => {});
  }, []);

  if (events.length === 0) return null;

  return (
    <section id="events" className="border-t border-stone_warm bg-[#F3F0EB]/60 py-24 lg:py-32" data-testid="events-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-dark"
        >
          Guild Gatherings
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="mb-16 font-serif text-4xl font-light tracking-tighter text-charcoal sm:text-5xl"
        >
          Upcoming <span className="italic text-gold-dark">moments.</span>
        </motion.h2>

        <div>
          {events.map((ev, i) => {
            const { day, month } = formatDate(ev.event_date);
            return (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
                className="grid grid-cols-1 gap-4 border-t border-stone_warm py-8 md:grid-cols-12 md:items-center md:gap-8"
                data-testid={`event-item-${ev.id}`}
              >
                <div className="flex items-baseline gap-3 md:col-span-2 md:flex-col md:gap-0">
                  <span className="font-serif text-5xl font-light tracking-tighter text-charcoal">{day}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold-dark">{month}</span>
                </div>
                <div className="md:col-span-7">
                  <h3 className="font-serif text-2xl font-medium tracking-tight text-charcoal">{ev.title}</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-charcoal/65">{ev.description}</p>
                </div>
                <div className="md:col-span-3 md:text-right">
                  {ev.location && (
                    <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/55 md:justify-end">
                      <MapPin size={13} className="text-gold-dark" />{ev.location}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
